import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Psychology from '@mui/icons-material/Psychology';
import Security from '@mui/icons-material/Security';
import Storage from '@mui/icons-material/Storage';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import AccessTime from '@mui/icons-material/AccessTime';
import Place from '@mui/icons-material/Place';
import Group from '@mui/icons-material/Group';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import GetApp from '@mui/icons-material/GetApp';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import SchoolIcon from '@mui/icons-material/School';
import { useTheme } from '@mui/material/styles';

// Helper components adapted to MUI
const Section = ({ id, children, sx = {} }) => (
  <Box id={id} sx={{ py: { xs: 6, md: 8 }, ...sx }}>
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const Feature = ({ Icon, title, children }) => (
  <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
    <CardHeader
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'action.hover', width: 36, height: 36 }}>
            <Icon fontSize="small" />
          </Avatar>
          <Typography variant="h6">{title}</Typography>
        </Box>
      }
      sx={{ pb: 0.5 }}
    />
    <CardContent>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </CardContent>
  </Card>
);

const Stat = ({ value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="h4" fontWeight={700}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const Item = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <CheckCircle sx={{ fontSize: 18, mt: '2px', color: 'success.main' }} />
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const PriceCard = ({ title, price, features, cta, badge }) => (
  <Card elevation={3} sx={{ borderRadius: 2, position: 'relative' }}>
    {badge && (
      <Chip label={badge} color="secondary" size="small" sx={{ position: 'absolute', top: -10, right: 16 }} />
    )}
    <CardHeader title={title} />
    <CardContent>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>{price}</Typography>
      <Stack spacing={1.2} sx={{ mb: 2 }}>
        {features.map((f, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 18, mt: '2px', color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary">{f}</Typography>
          </Box>
        ))}
      </Stack>
      <Button fullWidth variant="contained">{cta}</Button>
    </CardContent>
  </Card>
);

export default function Trainings() {
  const theme = useTheme();
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [conceptEmail, setConceptEmail] = useState('');
  const [conceptOpen, setConceptOpen] = useState(false);
  const [conceptReady, setConceptReady] = useState(false);

  const thisHolidayWindow = useMemo(() => 'Nov–Dec 2025', []);

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!agree) {
      alert('Please accept the privacy note.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 900);
  };

  const handleConceptSubmit = (e) => {
    e.preventDefault();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(conceptEmail);
    if (!ok) {
      alert('Enter a valid email');
      return;
    }
    setConceptReady(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* HERO */}
      <Section id="top" sx={{ pt: { xs: 6, md: 8 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Chip label={`Holiday cohorts • ${thisHolidayWindow}`} color="default" variant="outlined" sx={{ borderRadius: 2 }} />
            <Typography variant="h3" fontWeight={700} sx={{ mt: 2 }}>
              From overwhelmed to <Box component="span" sx={{ color: 'primary.main' }}>AI‑confident</Box> in 3 days
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              A hands‑on Kenyan‑context bootcamp for headteachers, HODs, ICT champions and class teachers. Learn to plan faster, differentiate better, and keep school data safe—without the jargon.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap' }}>
              <Button href="#contact" variant="contained" endIcon={<ArrowRightAlt />}>
                Book a cohort
              </Button>
              <Button href="#program" variant="outlined">See the program</Button>
              <Button variant="outlined" startIcon={<GetApp />} onClick={() => setConceptOpen(true)}>Concept note</Button>
            </Stack>
            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={4}><Stat value="3 days" label="Intensive, practical" /></Grid>
              <Grid item xs={4}><Stat value="30–60" label="Teachers per cohort" /></Grid>
              <Grid item xs={4}><Stat value="Certificate" label="Meira + Partner" /></Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader title={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SchoolIcon fontSize="small" /> Program at a glance</Box>} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <CalendarMonth sx={{ mt: '2px' }} />
                      <Box>
                        <Typography fontWeight={600}>{thisHolidayWindow}</Typography>
                        <Typography variant="body2" color="text.secondary">Weekday & Saturday options</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <AccessTime sx={{ mt: '2px' }} />
                      <Box>
                        <Typography fontWeight={600}>9:00 – 4:00 EAT</Typography>
                        <Typography variant="body2" color="text.secondary">Tea & lunch provided</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Place sx={{ mt: '2px' }} />
                      <Box>
                        <Typography fontWeight={600}>Nairobi + On‑site</Typography>
                        <Typography variant="body2" color="text.secondary">We come to clusters (≥25)</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Group sx={{ mt: '2px' }} />
                      <Box>
                        <Typography fontWeight={600}>Small‑group coaching</Typography>
                        <Typography variant="body2" color="text.secondary">Hands‑on labs & mini‑projects</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>

      {/* WHY */}
      <Section id="why">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><Feature Icon={Psychology} title="AI in the classroom">Lesson planning, differentiation, feedback, and assessment item generation aligned to CBC & international curricula—plus ethical, age‑appropriate use of AI with learners.</Feature></Grid>
          <Grid item xs={12} md={4}><Feature Icon={Storage} title="Data made useful">Turn attendance, assessment, and behavior records into decisions using simple dashboards. Understand Kenya’s Data Protection Act in plain English.</Feature></Grid>
          <Grid item xs={12} md={4}><Feature Icon={Security} title="Cybersecurity hygiene">Practical password & phishing defense, safer WhatsApp/email practices, backups, and a simple incident response one‑pager designed for schools.</Feature></Grid>
        </Grid>
      </Section>

      

      {/* OUTCOMES */}
      <Section id="outcomes">
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardHeader title="What schools say" />
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                    “The most practical training we’ve had—teachers used the tools the next day.”
                    <Box component="span" sx={{ display: 'block', fontSize: 12 }}>— Headteacher, Nairobi</Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                    “Finally, simple data dashboards we can maintain without a tech team.”
                    <Box component="span" sx={{ display: 'block', fontSize: 12 }}>— HOD Academics, Kiambu</Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                    “The cybersecurity drills were an eye‑opener for our staff and parents.”
                    <Box component="span" sx={{ display: 'block', fontSize: 12 }}>— ICT Lead, Nakuru</Box>
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>

      {/* PRICING */}
      <Section id="pricing">
        <Box sx={{ maxWidth: 780 }}>
          <Typography variant="h4" fontWeight={700}>Investment</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>Transparent tiers for individuals, schools, and clusters. All plans include materials, meals, and certificates.</Typography>
        </Box>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}><PriceCard title="Individual" price="KES —" cta="Reserve seat" features={["1 teacher", "All sessions", "Materials & meals", "Certificate"]} /></Grid>
          <Grid item xs={12} md={4}><PriceCard title="School Bundle" price="KES —" badge="Popular" cta="Request invoice" features={["10–20 teachers", "Group facilitation", "AUP/SOP tailoring", "PLC access (3 mo)"]} /></Grid>
          <Grid item xs={12} md={4}><PriceCard title="Cluster / County" price="Custom" cta="Talk to us" features={["≥ 50 teachers", "On‑site option", "Co‑branding", "Scholarship seats (if sponsored)"]} /></Grid>
        </Grid>
      </Section>

      {/* FAQ */}
      <Section id="faqs">
        <Box sx={{ maxWidth: 780 }}>
          <Typography variant="h4" fontWeight={700}>FAQs</Typography>
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Who should attend?</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">Headteachers, HODs, ICT champions, class teachers (primary & secondary), and edtech leads.</Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Do I need to be ‘techy’?</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">No. We start from first principles, keep it practical, and provide step‑by‑step templates.</Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Will I get resources after?</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">Yes. You’ll receive templates, a curated AI toolkit, and optional PLC support with monthly office hours.</Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Can you train on‑site at our school or cluster?</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">Yes. We run on‑site cohorts for groups of ≥ 25 teachers anywhere in Kenya.</Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Section>

      {/* CONTACT */}
      <Section id="contact">
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={7}>
            <Typography variant="h4" fontWeight={700}>Book your cohort</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>Tell us a bit about your school and preferred dates. We’ll call you within one business day (Mon–Fri, Nairobi time).</Typography>
            <Box component="form" onSubmit={handleLeadSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Full name" name="name" fullWidth required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Work email" name="email" type="email" fullWidth required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Phone / WhatsApp" name="phone" type="tel" fullWidth required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="School / Organisation" name="school" fullWidth required /></Grid>
                <Grid item xs={12}><TextField label="Preferred dates / notes" name="message" fullWidth multiline rows={4} placeholder={`Cohort during ${thisHolidayWindow}; on‑site in Nakuru; 40 teachers…`} /></Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
                    label={<Typography variant="body2" color="text.secondary">I agree to be contacted about this training and acknowledge Meira’s privacy notice (Kenya Data Protection Act compliant).</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button type="submit" variant="contained" disabled={submitting || submitted || !agree}>
                    {submitted ? 'Thanks — we’ll be in touch' : submitting ? 'Submitting…' : 'Request a call'}
                  </Button>
                  <Button variant="outlined" startIcon={<PhoneIcon />} href="tel:+254722545440">Call us</Button>
                  <Button variant="outlined" startIcon={<MailIcon />} href="mailto:hello@meira.africa">Email</Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader title="What’s included" />
              <CardContent>
                <Stack spacing={1.25}>
                  <Item text="Hands‑on labs & mini‑projects" />
                  <Item text="Templates: Schemes, rubrics, AUP/SOPs" />
                  <Item text="Cyber drills & incident playbook" />
                  <Item text="Meals & venue or on‑site facilitation" />
                  <Item text="Meira + Partner certificate" />
                  <Item text="Optional PLC (monthly office hours)" />
                </Stack>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardHeader titleTypographyProps={{ variant: 'subtitle1' }} title="Cohort size" sx={{ pb: 0 }} />
                      <CardContent sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">30–60 teachers</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardHeader titleTypographyProps={{ variant: 'subtitle1' }} title="Timing" sx={{ pb: 0 }} />
                      <CardContent sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">9:00–4:00 EAT</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>

      {/* Concept Note Dialog */}
      <Dialog open={conceptOpen} onClose={() => setConceptOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Get the detailed concept note</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleConceptSubmit} sx={{ mt: 1 }}>
            <TextField
              id="conceptEmail"
              label="Work email"
              type="email"
              placeholder="name@school.ac.ke"
              value={conceptEmail}
              onChange={(e) => setConceptEmail(e.target.value)}
              fullWidth
              required
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              We’ll send occasional updates about cohorts. You can unsubscribe any time.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {!conceptReady ? (
            <Button onClick={handleConceptSubmit} variant="contained">Email me the PDF</Button>
          ) : (
            <Button component="a" href="#" download variant="contained">Download concept note</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
